import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LOGO_HEIGHT, LOGO_URL, LOGO_WIDTH } from '@/lib/brand';
import {
  adminLoginUrl,
  configureAdminApi,
  fetchAdminSession,
  logoutAdmin,
} from '@/services/adminApi';
import { AdminSessionContext } from '@/hooks/useAdminSession';
import type { AdminSession } from '@/types/admin';

const LOGIN_ERRORS: Record<string, string> = {
  denied: 'auth.errors.denied',
  failed: 'auth.errors.failed',
  not_staff: 'auth.errors.not_staff',
  scope_denied: 'auth.errors.scope_denied',
  unavailable: 'auth.errors.unavailable',
};

function adminNavClassName({ isActive }: { isActive: boolean }): string {
  if (isActive) {
    return 'nav-link nav-link-active';
  }
  return 'nav-link';
}

/* Admin masthead: wordmark and the admin title on the left, small-caps navigation on the right. */
function AdminMasthead({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('admin');
  return (
    <header className="global-container">
      {/* Same bottom-aligned masthead as the public site; see Header.tsx for the 0.17em shift. */}
      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3 border-b-2 border-ink pb-3 pt-5 md:pt-6">
        <Link to="/admin" className="flex items-end gap-3" aria-label={t('nav.title')}>
          <img
            src={LOGO_URL}
            width={LOGO_WIDTH}
            height={LOGO_HEIGHT}
            alt="TOGAPhotos"
            className="block h-5 w-auto select-none md:h-6"
          />
          <span className="translate-y-[0.17em] font-serif text-lg leading-none tracking-[-0.01em] md:text-xl md:leading-none" aria-hidden="true">
            {t('nav.title')}
          </span>
        </Link>
        {children}
      </div>
      <div className="mt-0.5 h-px bg-rule" aria-hidden="true" />
    </header>
  );
}

/* Narrow single-column panel for the sign-in, disabled and unavailable states. */
function NarrowPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <AdminMasthead>
        <div className="flex items-center text-xs uppercase leading-6 tracking-[0.14em]">
          <LanguageSwitcher />
        </div>
      </AdminMasthead>
      <main className="global-container flex-1 py-10 md:py-14">
        <div className="max-w-md border-t-2 border-ink pt-4">
          <h1 className="m-0 text-[1.75rem] md:text-[2rem]">{title}</h1>
          <div className="mt-4 space-y-4 text-sm leading-relaxed text-ink-2">{children}</div>
        </div>
      </main>
    </div>
  );
}

function LoginScreen({ next }: { next: string }) {
  const { t } = useTranslation('admin');
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get('login_error');
  let errorMessage: string | null = null;
  if (errorCode) {
    errorMessage = LOGIN_ERRORS[errorCode] ?? LOGIN_ERRORS.failed;
  }

  return (
    <NarrowPanel title={t('auth.title')}>
      <p className="m-0">{t('auth.intro')}</p>
      {errorMessage && (
        <p role="alert" className="callout callout-danger m-0">
          {t(errorMessage)}
        </p>
      )}
      <Button asChild className="w-full">
        <a href={adminLoginUrl(next)}>{t('auth.signIn')}</a>
      </Button>
      <p className="m-0">
        <Link to="/" className="text-link">
          {t('auth.back')}
        </Link>
      </p>
    </NarrowPanel>
  );
}

export function AdminLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const [session, setSession] = useState<AdminSession | null>(null);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    fetchAdminSession(controller.signal)
      .then((value) => {
        configureAdminApi(value.csrf_token ?? '', refresh);
        setSession(value);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setError(true);
        }
      });
    return () => controller.abort();
  }, [reloadToken, refresh]);

  const contextValue = useMemo(() => {
    if (!session?.authenticated || !session.user) {
      return null;
    }
    return { session, user: session.user, refresh };
  }, [session, refresh]);

  const handleLogout = async () => {
    try {
      await logoutAdmin();
    } finally {
      refresh();
    }
  };

  if (error) {
    return (
      <NarrowPanel title={t('auth.title')}>
        <p className="m-0">{t('auth.unavailable')}</p>
        <Button variant="outline" onClick={refresh}>
          {t('retry')}
        </Button>
      </NarrowPanel>
    );
  }
  if (!session) {
    return (
      <div className="flex min-h-screen flex-col">
        <AdminMasthead />
        <main className="global-container flex-1" aria-busy="true">
          <p className="kicker py-16">{t('loading')}</p>
        </main>
      </div>
    );
  }
  if (!session.enabled) {
    return (
      <NarrowPanel title={t('auth.disabledTitle')}>
        <p className="m-0">{t('auth.disabled')}</p>
        <p className="m-0">
          <Link to="/" className="text-link">
            {t('auth.back')}
          </Link>
        </p>
      </NarrowPanel>
    );
  }
  if (!contextValue) {
    return <LoginScreen next={`${location.pathname}${location.search}`} />;
  }

  return (
    <AdminSessionContext.Provider value={contextValue}>
      <div className="flex min-h-screen flex-col">
        <AdminMasthead>
          {/* Phones: the nav and account rows take their own line under the wordmark. */}
          <div className="flex w-full flex-wrap items-center justify-between gap-x-8 gap-y-2 text-xs uppercase leading-6 tracking-[0.14em] md:w-auto md:min-w-0 md:flex-1">
            <nav className="flex flex-wrap items-center gap-x-5 gap-y-1" aria-label={t('nav.label')}>
              <NavLink to="/admin" end className={adminNavClassName}>
                {t('nav.events')}
              </NavLink>
              <NavLink to="/admin/events/new" className={adminNavClassName}>
                {t('nav.new')}
              </NavLink>
              <NavLink to="/admin/crawlers" className={adminNavClassName}>
                {t('nav.crawlers')}
              </NavLink>
              <NavLink to="/admin/audit" className={adminNavClassName}>
                {t('nav.audit')}
              </NavLink>
            </nav>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              <LanguageSwitcher />
              <span className="normal-case tracking-normal text-ink-2">
                {contextValue.user.name}
                <span className="kicker ml-2">{contextValue.user.role}</span>
              </span>
              <Link to="/" className="nav-link">
                {t('nav.viewSite')}
              </Link>
              <button type="button" className="nav-link" onClick={handleLogout}>
                {t('nav.signOut')}
              </button>
            </div>
          </div>
        </AdminMasthead>
        <main className="global-container flex-1 py-8 md:py-10">{children}</main>
      </div>
    </AdminSessionContext.Provider>
  );
}
